import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { useState, useMemo } from "react";
import { tokens } from "../../../theme";
import { EquipmentRental } from "./tabs/RentalsTab";

interface ReturnGearDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReturnFormData) => Promise<void>;
  rental: EquipmentRental | null;
}

export interface ReturnFormData {
  returnDate: string;
  conditionAfter: string;
  notes: string;
  isOverdue: boolean;
}

const CONDITION_OPTIONS = ["new", "good", "fair", "poor", "damaged"] as const;

const initialForm: ReturnFormData = {
  returnDate: new Date().toISOString().split("T")[0],
  conditionAfter: "",
  notes: "",
  isOverdue: false,
};

export default function ReturnGearDialog({
  open,
  onClose,
  onSubmit,
  rental,
}: ReturnGearDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form, setForm] = useState<ReturnFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-detect overdue based on entered return date vs rental_end_date
  const overdueInfo = useMemo(() => {
    if (!rental?.rental_end_date || !form.returnDate) return null;
    const endDate = new Date(rental.rental_end_date);
    const returnDate = new Date(form.returnDate);
    const diffMs = returnDate.getTime() - endDate.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) {
      return { overdue: true, days: diffDays };
    }
    return { overdue: false, days: 0 };
  }, [form.returnDate, rental?.rental_end_date]);

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await onSubmit({
      ...form,
      isOverdue: overdueInfo?.overdue ?? false,
    });
    setIsSubmitting(false);
    setForm(initialForm);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 20px",
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 0.8,
      },
    },
  };

  const textAreaSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: colors.primary[900],
          boxShadow: "0px 10px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 20,
          top: 20,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "50%",
          width: 26,
          height: 26,
          color: theme.palette.text.primary,
          zIndex: 10,
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <DialogContent sx={{ p: "24px", overflowX: "hidden" }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color={colors.primary[400]}
          mb={3}
        >
          Return Gear
        </Typography>

        {/* Rental Summary Card */}
        {rental && (
          <Box
            sx={{
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              p: "16px 20px",
              mb: 3,
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography fontWeight={500} fontSize={14} color={theme.palette.text.primary}>
                Rental #{rental.id}
              </Typography>
              <Box
                sx={{
                  fontSize: 11,
                  color: rental.status === "overdue" ? "#EF4444" : "#1D9E75",
                  background: rental.status === "overdue" ? "#FEE2E2" : "#E1F5EE",
                  px: "10px",
                  py: "4px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {rental.status}
              </Box>
            </Box>

            <Typography fontSize={13} color={theme.palette.text.secondary} mb={1}>
              {[rental.contact_name, rental.contact_last_name].filter(Boolean).join(" ") ||
                String(rental.client_name || "—")}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Box
                sx={{
                  background: colors.primary[800],
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "20px",
                  fontSize: 11,
                  color: theme.palette.text.secondary,
                }}
              >
                {formatDate(rental.rental_start_date)} - {formatDate(rental.rental_end_date)}
              </Box>
              {rental.daily_rate && (
                <Box
                  sx={{
                    background: colors.primary[800],
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "20px",
                    fontSize: 11,
                    color: theme.palette.text.secondary,
                  }}
                >
                  ${Number(rental.daily_rate).toFixed(2)} / day
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Actual Return Date */}
        <Typography variant="body2" color={theme.palette.text.secondary} display="block" mb={1}>
          Actual Return Date <span style={{ color: "#E24B4A" }}>*</span>
        </Typography>
        <TextField
          type="date"
          fullWidth
          value={form.returnDate}
          onChange={(e) => setForm((p) => ({ ...p, returnDate: e.target.value }))}
          sx={{ ...fieldSx, mb: 2 }}
        />

        {/* Overdue / On-time indicator */}
        {overdueInfo !== null && form.returnDate && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "10px 16px",
              borderRadius: "12px",
              mb: 2.5,
              background: overdueInfo.overdue ? "#FEE2E2" : "#E1F5EE",
            }}
          >
            {overdueInfo.overdue ? (
              <>
                <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#EF4444" }} />
                <Typography fontSize={13} color="#EF4444" fontWeight={500}>
                  Overdue by {overdueInfo.days} day{overdueInfo.days !== 1 ? "s" : ""} —
                  overdue date will be recorded as {formatDate(form.returnDate)}.
                </Typography>
              </>
            ) : (
              <>
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: "#1D9E75" }} />
                <Typography fontSize={13} color="#1D9E75" fontWeight={500}>
                  Returned on time.
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Condition After */}
        <Typography variant="body2" color={theme.palette.text.secondary} display="block" mb={1}>
          Condition on Return <span style={{ color: "#E24B4A" }}>*</span>
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2.5}>
          {CONDITION_OPTIONS.map((cond) => (
            <Box
              key={cond}
              onClick={() => setForm((p) => ({ ...p, conditionAfter: cond }))}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: "20px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                border: `1px solid ${
                  form.conditionAfter === cond
                    ? colors.primary[400]
                    : theme.palette.divider
                }`,
                background:
                  form.conditionAfter === cond
                    ? colors.primary[800]
                    : "transparent",
                color:
                  form.conditionAfter === cond
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                transition: "all 0.15s ease",
                textTransform: "capitalize",
              }}
            >
              {cond}
            </Box>
          ))}
        </Box>

        {/* Notes */}
        <Typography variant="body2" color={theme.palette.text.secondary} display="block" mb={1}>
          Notes
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Any remarks about the return..."
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          sx={{ ...textAreaSx, mb: 4 }}
        />

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !form.returnDate || !form.conditionAfter}
            style={{
              background: colors.primary[400],
              color: colors.primary[900],
              border: "none",
              borderRadius: "12px",
              padding: "14px 28px",
              fontSize: 15,
              fontWeight: 500,
              cursor: isSubmitting || !form.returnDate || !form.conditionAfter ? "not-allowed" : "pointer",
              opacity: isSubmitting || !form.returnDate || !form.conditionAfter ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "opacity 0.2s ease",
            }}
          >
            {isSubmitting && <CircularProgress size={16} color="inherit" />}
            {isSubmitting ? "Processing..." : "Confirm Return"}
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}